import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import type { FC } from "react";
import { COVERAGE } from "../../../theme/tokens";
import { PtoCornerFold } from "./PtoCornerFold";

/**
 * Coverage status of a single staffing slot on a single day, derived from
 * `filled` against two thresholds (`minimum` safety floor, `target` ideal).
 * Exposed so callers can read the tier without re-deriving it.
 */
export enum CoverageTier {
	/** filled < minimum — below the safety floor. */
	Critical = "critical",
	/** minimum <= filled < target — floor met, under target. */
	Warning = "warning",
	/** filled === target — exactly met (near-muted; only problems carry weight). */
	Met = "met",
	/** filled > target on a required slot — over-staffed (faint positive tint). */
	Surplus = "surplus",
	/** off day / inactive — never a gap. */
	NotRequired = "notRequired",
	/** filled > 0 where target is 0 — unplanned coverage on an off day. */
	SurplusOnOffDay = "surplusOnOffDay",
}

export type CoverageDeltaInput = {
	/** Bodies counted as filling the slot that day — the caller decides whether PTO counts. */
	filled: number;
	/** Target / ideal staffing for that slot+day. */
	target: number;
	/** Minimum safety floor for that slot+day. */
	minimum: number;
	/** False when the slot is not required that day (off day / inactive week). */
	required: boolean;
};

export const resolveCoverageTier = ({ filled, target, minimum, required }: CoverageDeltaInput): CoverageTier => {
	if (!required) {
		return filled > 0 ? CoverageTier.SurplusOnOffDay : CoverageTier.NotRequired;
	}
	if (filled < minimum) return CoverageTier.Critical;
	if (filled < target) return CoverageTier.Warning;
	if (filled > target) return CoverageTier.Surplus;
	return CoverageTier.Met;
};

/**
 * Canonical tier → presentation mapping. The single source of truth for every
 * tier's swatch + optional leading severity bar; the cell renders from it, and
 * `LEGEND_ITEMS` is derived from it so a legend can never drift from the cells.
 *
 * `bar` is the left-edge severity accent: every OFF-target tier — under (deficit)
 * AND over (surplus) — carries one, because over-staffing is a problem too (wasted
 * coverage), not a positive. Only exactly-Met and Not-Required recede bar-less. The
 * signed number (e.g. -1 vs +2) tells direction; the amber signals "off target".
 */
export const TIER_COLORS: Record<CoverageTier, { bg: string; ink: string; bar?: string }> = {
	[CoverageTier.Critical]: { bg: COVERAGE.CRITICAL_BG, ink: COVERAGE.CRITICAL_INK, bar: COVERAGE.CRITICAL_BAR },
	[CoverageTier.Warning]: { bg: COVERAGE.WARNING_BG, ink: COVERAGE.WARNING_INK, bar: COVERAGE.WARNING_BAR },
	[CoverageTier.Met]: { bg: COVERAGE.MET_BG, ink: COVERAGE.MET_INK },
	// Over target / on an off day = over-staffed → flagged amber with a bar (same logic as a deficit).
	[CoverageTier.Surplus]: { bg: COVERAGE.WARNING_BG, ink: COVERAGE.WARNING_INK, bar: COVERAGE.WARNING_BAR },
	[CoverageTier.NotRequired]: { bg: COVERAGE.MUTED_BG, ink: COVERAGE.MUTED_INK },
	[CoverageTier.SurplusOnOffDay]: { bg: COVERAGE.WARNING_BG, ink: COVERAGE.WARNING_INK, bar: COVERAGE.WARNING_BAR },
};

/**
 * Legend rows for the coverage tiers, in display order, each carrying its
 * swatch (from `TIER_COLORS`) and its i18n label key. Driving a legend from
 * this guarantees it stays in lockstep with the cells — adding a tier here is
 * the only edit needed.
 */
export const LEGEND_ITEMS: readonly { tier: CoverageTier; bg: string; ink: string; bar?: string; labelKey: string }[] = [
	{ tier: CoverageTier.Critical, labelKey: "schedule.template.v3.legend.critical" },
	{ tier: CoverageTier.Warning, labelKey: "schedule.template.v3.legend.warning" },
	{ tier: CoverageTier.Met, labelKey: "schedule.template.v3.legend.met" },
	{ tier: CoverageTier.Surplus, labelKey: "schedule.template.v3.legend.over" },
	{ tier: CoverageTier.SurplusOnOffDay, labelKey: "schedule.template.v3.legend.surplus" },
	{ tier: CoverageTier.NotRequired, labelKey: "schedule.template.v3.legend.notRequired" },
].map(({ tier, labelKey }) => ({ tier, labelKey, ...TIER_COLORS[tier] }));

/**
 * How the cell's number reads:
 *  - `"delta"` — the signed `filled − target` (e.g. `-1`, `+2`, `0`). Compact,
 *    surfaces only the gap/surplus magnitude. The default.
 *  - `"ratio"` — `filled / target` ("have / need", e.g. `3/4`). Reads better than
 *    a bare `0` because it shows both the supply and the demand directly.
 *  - `"filled"` — the bare `filled` count (e.g. `18`). For surfaces where the
 *    reader asked for "how many we have", not "how many we need" — the demand is
 *    deliberately absent from the glyph, and the TIER TINT is what still says the
 *    day is short (a red `18` is a gap), so this mode is not a plain number: it is
 *    a coloured one. Because the glyph no longer carries the target, callers MUST
 *    pass an `ariaLabel` that names both `filled` and `target`, or the demand is
 *    lost for anyone who cannot see the tint — `Props` is a union that makes the
 *    compiler enforce that, rather than leaving it to be read here.
 * The two-threshold COLOR is identical in all three modes — only the glyph changes.
 */
export type CoverageCellDisplayMode = "delta" | "ratio" | "filled";

/**
 * How loudly the cell states its tier — the SAME tier math either way, two different jobs on the page.
 *
 * - `"severity"` (default) — the grading IS the content. The tier owns the fill, the ink and the weight
 *   ladder, so a wall of cells reads as a heat map and the gaps find the eye first. Right for a coverage
 *   grid, where every cell exists to be compared against its target.
 * - `"count"` — the NUMBER is the content, and nothing else competes with it. One neutral surface, one
 *   ink, one weight, no severity rail and no meter: the cell states a headcount and stops. Right where
 *   the reader asked "how many people", not "how far off target". A red-on-red headcount reads as an
 *   error the reader has to dismiss before they can read the number, and where a whole month of cells is
 *   nominally short of a summed target a severity fill stops discriminating at all — everything is red,
 *   so nothing is. The demand has not stopped mattering; it stops being drawn here, and stays legible in
 *   the `"ratio"` label and the `ariaLabel`, which always names both counts.
 *
 * Additive and opt-in: omitting it leaves every existing caller on the severity presentation unchanged.
 */
export type CoverageCellEmphasis = "severity" | "count";

type BaseProps = CoverageDeltaInput & {
	/** See `CoverageCellEmphasis`. Defaults to `"severity"` — the grading-first presentation. */
	emphasis?: CoverageCellEmphasis;
	/** How many assigned bodies to flag as on PTO (drives the corner-fold marker). Which absences are
	 * worth marking is the caller's call — this cell only renders the fold when the count is > 0. */
	ptoCount?: number;
	/** Click handler — opens the fix-in-place affordance the caller provides. */
	onClick?: (event: React.MouseEvent<HTMLElement>) => void;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * `ariaLabel` is optional only for the two modes whose glyph carries the target: `"ratio"` prints
 * `filled/target` outright and `"delta"` prints the signed distance to it, so a reader who cannot see
 * the tint still has the demand.
 *
 * `"filled"` prints the bare supply, which makes the label the ONLY place the target survives — the
 * docblock below has always said callers MUST pass one, and this union is what makes the compiler say
 * it too. Modelled as "any mode, with a label" rather than "filled, with a label" so a caller holding a
 * runtime `CoverageCellDisplayMode` (the Coverage grid's column-wide toggle) still type-checks: passing
 * a label unconditionally satisfies the strictest mode it could be.
 */
type Props = BaseProps &
	(
		| {
				/** How the number reads — signed delta (default) or a `have/need` ratio; both print the target. */
				displayMode?: Exclude<CoverageCellDisplayMode, "filled">;
				/** Accessible label (already translated) — the cell is color + number + this. */
				ariaLabel?: string;
		  }
		| {
				/** How the number reads. Any mode, `"filled"` included, once a label is supplied. */
				displayMode: CoverageCellDisplayMode;
				/** Required here: `"filled"` omits the target from the glyph, so this is the only place it survives. */
				ariaLabel: string;
		  }
	);

/**
 * One coverage cell on a two-threshold diverging severity scale (red below
 * minimum, amber below target, neutral at/over). The number reads as a signed
 * `filled − target` delta, a `filled / target` ("have/need") ratio, or the bare
 * `filled` count — see `displayMode`; the color is the same in every mode, so
 * even the bare-count mode still says how far off target the day is. Whenever the
 * glyph omits the target (`"delta"`, `"filled"`), the caller's `ariaLabel` is the
 * only place the demand survives — pass one that names filled AND target.
 *
 * Scannability is the whole job: the two deficit tiers carry a left severity
 * bar so a column of gaps reads as a continuous edge, the deepest gaps carry the
 * heaviest ink, and the exactly-met state recedes to almost nothing — only
 * problems and surplus draw the eye. A corner-fold marks a PTO-driven dip.
 *
 * Generic over any supply-vs-demand grid (staffing template, daily coverage,
 * Synergy visits) — it owns only the tier math + presentation, never page data.
 * Never color-alone: the number + an aria-label accompany the color.
 */
export const CoverageDeltaCell: FC<Props> = ({
	filled,
	target,
	minimum,
	required,
	ptoCount = 0,
	displayMode = "delta",
	emphasis = "severity",
	onClick,
	ariaLabel,
	dataTestId,
	sx,
}) => {
	const tier = resolveCoverageTier({ filled, target, minimum, required });
	const tierColors = TIER_COLORS[tier];
	const delta = filled - target;
	const isInteractive = !!onClick;
	const isCountEmphasis = emphasis === "count";

	// In count emphasis the number is the content, so it is always drawn — including on a day with no
	// requirement, where the severity presentation deliberately renders nothing because it has nothing to
	// grade. "Nothing to grade" is not "nothing to say" once the reader is asking for a headcount.
	const showNumber = isCountEmphasis || tier !== CoverageTier.NotRequired;
	const isCritical = tier === CoverageTier.Critical;
	const isMet = tier === CoverageTier.Met;
	const bg = isCountEmphasis ? COVERAGE.COUNT_BG : tierColors.bg;
	const ink = isCountEmphasis ? COVERAGE.COUNT_INK : tierColors.ink;
	// The LEFT severity rail belongs to the severity presentation alone. Count emphasis replaces it with
	// the bottom meter below, for two reasons the monthly grid made plain: a bare vertical tick sitting
	// beside a glyph reads as a divider rather than as a grade, and a rail is binary — it fires on every
	// tile in a month whose days are all nominally short, which is a signal that has stopped signalling.
	const bar = isCountEmphasis ? undefined : tierColors.bar;
	/**
	 * Coverage as a PROPORTION, drawn as a hairline along the bottom of the count — the count emphasis's
	 * entire carrier of the target.
	 *
	 * A length says what a colour alone cannot: `12` and `84` against a target of 97 are both "short",
	 * and only the meter distinguishes them at a glance. That is the difference between a warning and
	 * information, which is the whole point of this emphasis — the reader gets the demand back without
	 * being told they have an error. `undefined` where there is no requirement: nothing to measure
	 * against, and inventing a full bar there would assert a coverage state that does not exist.
	 */
	// Ratio reads "have/need"; filled reads the bare supply; delta reads the signed gap (explicit `+`).
	const label = displayMode === "ratio" ? `${filled}/${target}` : displayMode === "filled" ? `${filled}` : delta > 0 ? `+${delta}` : `${delta}`;
	// Weight ladder: critical owns the scan (heaviest), surplus/warning carry
	// moderate weight, the exactly-met "no problem" state is the lightest. Count
	// emphasis has no ladder at all — every headcount is stated with one voice.
	const weight = isCountEmphasis ? 700 : isCritical ? 750 : isMet ? 500 : 650;

	return (
		<Box
			component={isInteractive ? "button" : "div"}
			type={isInteractive ? "button" : undefined}
			onClick={onClick}
			aria-label={ariaLabel}
			data-testid={dataTestId}
			data-coverage-tier={tier}
			sx={[
				(theme) => ({
					position: "relative",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					width: "100%",
					height: "100%",
					minHeight: 28,
					border: "none",
					// Pad in from the severity bar so the number stays optically centered
					// in the cell rather than centered on the remaining space.
					p: 0,
					pl: bar ? "3px" : 0,
					m: 0,
					font: "inherit",
					backgroundColor: bg,
					color: ink,
					cursor: isInteractive ? "pointer" : "default",
					outline: "none",
					transition:
						"filter 120ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms cubic-bezier(0.23, 1, 0.32, 1)",
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover": isInteractive
							? // Lift the hovered cell off the lattice: a subtle inner ring + a touch
								// more saturation, no layout shift (inset shadow, not a border).
								{ filter: "brightness(0.97)", boxShadow: `inset 0 0 0 1px ${COVERAGE.CELL_HOVER_RING}`, zIndex: 1 }
							: {},
					},
					"&:active": isInteractive ? { transform: "scale(0.96)" } : {},
					// Keyboard focus ring — tokenized to the user's brand primary. Inset so
					// it stays visible inside the dense 1px grid lattice without clipping.
					"&:focus-visible": isInteractive ? { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px", zIndex: 1 } : {},
					"@media (prefers-reduced-motion: reduce)": { transition: "filter 120ms, box-shadow 120ms", "&:active": { transform: "none" } },
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{/* Left severity bar — the peripheral scan signal for deficit tiers. */}
			{bar && (
				<Box
					aria-hidden
					sx={{
						position: "absolute",
						top: 0,
						bottom: 0,
						left: 0,
						width: "3px",
						backgroundColor: bar,
					}}
				/>
			)}
			{showNumber && (
				<Typography
					component="span"
					sx={{
						// Critical carries a hair more size so the worst gaps read first; ratio
						// (two digits + slash) drops slightly so it never crowds the corner-fold.
						// `"filled"` is as short as a delta, so it shares that size ladder.
						fontSize: displayMode === "ratio" ? 12.5 : isCritical && !isCountEmphasis ? 14.5 : 14,
						fontWeight: weight,
						lineHeight: 1,
						fontVariantNumeric: "tabular-nums",
						letterSpacing: isCritical && !isCountEmphasis ? "0.01em" : 0,
						// The exactly-met state recedes hardest — it is the "nothing to see
						// here" cell and must not compete with a real gap two columns over.
						// Count emphasis never fades: a headcount is a headcount.
						opacity: isMet && !isCountEmphasis ? 0.6 : 1,
					}}
				>
					{label}
				</Typography>
			)}
			{ptoCount > 0 && (
				// Corner-fold glyph: a filled triangle in the top-right corner so a
				// PTO-driven dip is distinguishable from a plain gap at a glance,
				// without opening the popover. A thin surface-colored notch reads it
				// as a deliberate "folded corner" rather than a stray triangle.
				<PtoCornerFold size={10} />
			)}
		</Box>
	);
};
